import { normalizeCameraList } from "../camaras/model/cameraModel";
import axiosInstance from "core/api/axiosInstance";

// Función auxiliar para obtener info de autenticación
const getAuthInfo = () => {
  const token = localStorage.getItem("token");
  return { hasToken: !!token, token };
};

// ========== FUNCIÓN AUXILIAR PARA CONVERTIR DATOS BINARIOS A BASE64 ==========
const convertBinaryToBase64 = (binaryData) => {
  if (!binaryData) return null;

  try {
    // Si es ArrayBuffer
    if (binaryData instanceof ArrayBuffer) {
      const bytes = new Uint8Array(binaryData);
      const base64 = btoa(
        Array.from(bytes)
          .map((byte) => String.fromCharCode(byte))
          .join("")
      );
      return base64;
    }

    // Si es Uint8Array
    if (binaryData instanceof Uint8Array) {
      const base64 = btoa(
        Array.from(binaryData)
          .map((byte) => String.fromCharCode(byte))
          .join("")
      );
      return base64;
    }

    // Si es string binario
    if (typeof binaryData === "string") {
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i) & 0xff;
      }
      const base64 = btoa(
        Array.from(bytes)
          .map((byte) => String.fromCharCode(byte))
          .join("")
      );
      return base64;
    }

    // Si es array de números (bytes)
    if (Array.isArray(binaryData)) {
      const bytes = new Uint8Array(binaryData);
      const base64 = btoa(
        Array.from(bytes)
          .map((byte) => String.fromCharCode(byte))
          .join("")
      );
      return base64;
    }

    return null;
  } catch (error) {
    console.error("Error convirtiendo datos binarios a base64:", error);
    return null;
  }
};

// ========== FUNCIÓN AUXILIAR PARA VERIFICAR SI ES UNA IMAGEN VÁLIDA ==========
const isValidImage = (bytes) => {
  if (!bytes || bytes.length < 4) return false;

  // Verificar JPEG: FF D8
  const isJPEG = bytes[0] === 0xff && bytes[1] === 0xd8;

  // Verificar PNG: 89 50 4E 47
  const isPNG = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;

  // Verificar GIF: 47 49 46
  const isGIF = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;

  // Verificar BMP: 42 4D
  const isBMP = bytes[0] === 0x42 && bytes[1] === 0x4d;

  return isJPEG || isPNG || isGIF || isBMP;
};

// ========== FUNCIÓN AUXILIAR PARA DETECTAR MIME TYPE ==========
const detectImageMimeType = (bytes) => {
  if (!bytes || bytes.length < 4) return "image/jpeg";

  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "image/jpeg";
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47)
    return "image/png";
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return "image/gif";
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) return "image/bmp";

  return "image/jpeg";
};

// ========== SERVICIO PARA OBTENER LISTA DE COMERCIOS CON SUS CÁMARAS ==========
export const getCamerasListService = async () => {
  try {
    const authInfo = getAuthInfo();

    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const { data } = await axiosInstance.get("/api/automatization/camera/get-cameras");

    const result = normalizeCameraList(data);

    return {
      success: true,
      data: result,
      message: data.msm || "Comercios y cámaras obtenidos exitosamente",
      rawResponse: data,
    };
  } catch (error) {
    let errorMessage = error.message || "Error al obtener la lista de comercios y cámaras";

    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        error.response.data?.msm ||
        `Error ${error.response.status}: ${error.response.statusText}`;

      if (error.response.status === 401) {
        errorMessage = "No autorizado. Su sesión ha expirado o el token es inválido.";
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        setTimeout(() => {
          if (window.location.pathname !== "/authentication/sign-in") {
            window.location.href = "/authentication/sign-in";
          }
        }, 2000);
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor. Verifique su conexión a internet.";
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      message: errorMessage,
    };
  }
};

// ========== SERVICIO PARA OBTENER EL ESTADO DE UNA CÁMARA POR SU IP ==========
export const getCameraStatusService = async (ip) => {
  try {
    const authInfo = getAuthInfo();

    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!ip) {
      throw new Error("La IP de la cámara es requerida");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: 30000,
    };

    const response = await axiosInstance.post(
      "/api/automatization/camera/status-camera",
      { ip },
      config
    );

    const { data, status } = response;

    let isSuccess = false;
    let message = "Estado de cámara obtenido exitosamente";
    let deviceStatus = null;
    let rawData = data;

    if (data) {
      if (data.data && data.data.data && data.data.data.DeviceStatus) {
        deviceStatus = data.data.data.DeviceStatus;
        isSuccess = true;
        message = data.data.msg || data.msm || message;
      } else if (data.data && data.data.DeviceStatus) {
        deviceStatus = data.data.DeviceStatus;
        isSuccess = true;
        message = data.msm || data.message || message;
      } else if (data.DeviceStatus) {
        deviceStatus = data.DeviceStatus;
        isSuccess = true;
        message = data.msm || data.message || message;
      } else if (data.data && typeof data.data === "object") {
        const findDeviceStatus = (obj) => {
          if (!obj || typeof obj !== "object") return null;
          if (obj.DeviceStatus) return obj.DeviceStatus;
          for (const key in obj) {
            if (typeof obj[key] === "object") {
              const found = findDeviceStatus(obj[key]);
              if (found) return found;
            }
          }
          return null;
        };
        deviceStatus = findDeviceStatus(data.data);
        if (deviceStatus) {
          isSuccess = true;
        }
      }

      if (deviceStatus && status >= 200 && status < 300) {
        isSuccess = true;
      }
    }

    // Algunas versiones entregan las métricas directamente, sin DeviceStatus.
    if (!deviceStatus) {
      deviceStatus = [data?.data?.data, data?.data, data].find(
        (value) =>
          value &&
          typeof value === "object" &&
          (value.deviceUpTime !== undefined || value.CPUList || value.MemoryList)
      );
      if (deviceStatus) isSuccess = true;
    }

    if (
      deviceStatus &&
      typeof deviceStatus === "object" &&
      !Array.isArray(deviceStatus) &&
      Object.keys(deviceStatus).length > 0
    ) {
      const first = (value) => (Array.isArray(value) ? value[0] : value);
      const cpu = first(deviceStatus.CPUList?.CPU);
      const memory = first(deviceStatus.MemoryList?.Memory);
      const camera = first(deviceStatus.CameraList?.Camera);
      let uploads = [];
      if (deviceStatus?.uploadTimeConsumingList?.uploadTimeConsuming) {
        const uploadData = deviceStatus.uploadTimeConsumingList.uploadTimeConsuming;
        uploads = Array.isArray(uploadData) ? uploadData : [uploadData];
      }

      return {
        success: isSuccess,
        data: {
          ip: ip,
          queriedAt: new Date().toISOString(),
          version: deviceStatus?.version ?? "No disponible",
          uptime: deviceStatus?.deviceUpTime ?? "No disponible",
          bootTime: deviceStatus?.bootTime ?? "No disponible",
          cpuUtilization: cpu?.cpuUtilization ?? "No disponible",
          memoryUsage: memory?.memoryUsage ?? "No disponible",
          memoryAvailable: memory?.memoryAvailable ?? "No disponible",
          cacheSize: memory?.cacheSize ?? "No disponible",
          cameraRunTime: camera?.cameraRunTotalTime ?? "No disponible",
          lastCalibrationTime: deviceStatus?.lastCalibrationTime ?? "No disponible",
          uploads: uploads,
          raw: {
            deviceStatus: deviceStatus,
            fullResponse: rawData,
          },
        },
        message: message,
        timestamp: new Date().toISOString(),
        httpStatus: status,
      };
    }

    return {
      success: false,
      data: {
        ip: ip,
        version: "No disponible",
        uptime: "No disponible",
        bootTime: "No disponible",
        cpuUtilization: "No disponible",
        memoryUsage: "No disponible",
        memoryAvailable: "No disponible",
        cacheSize: "No disponible",
        cameraRunTime: "No disponible",
        lastCalibrationTime: "No disponible",
        uploads: [],
        raw: rawData,
      },
      error: "La respuesta no contiene un estado de cámara válido.",
      message: "La respuesta no contiene un estado de cámara válido.",
      timestamp: new Date().toISOString(),
      httpStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en getCameraStatusService:", error);

    let errorMessage = "Error al obtener el estado de la cámara";
    let shouldLogout = false;
    let httpStatus = null;

    if (error.response) {
      const responseData = error.response.data;
      httpStatus = error.response.status;

      if (typeof responseData === "string") {
        errorMessage = responseData;
      } else if (responseData?.msm) {
        errorMessage = responseData.msm;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (responseData?.error) {
        errorMessage = responseData.error;
      } else if (responseData?.data?.msg) {
        errorMessage = responseData.data.msg;
      } else {
        errorMessage = `Error ${httpStatus}: ${error.response.statusText}`;
      }

      if (httpStatus === 404) {
        errorMessage = "No se encontró la cámara con la IP proporcionada.";
      } else if (httpStatus === 400) {
        errorMessage = "Solicitud inválida. Verifique la IP enviada.";
      } else if (httpStatus === 401) {
        errorMessage = "No autorizado. Su sesión ha expirado o el token es inválido.";
        shouldLogout = true;
      } else if (httpStatus === 403) {
        errorMessage = "No tiene permisos para acceder a esta cámara.";
      } else if (httpStatus === 500) {
        errorMessage = "Error interno del servidor. Intente más tarde.";
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor. Verifique su conexión a internet.";
      httpStatus = 0;
    } else {
      errorMessage = error.message || "Error desconocido al obtener el estado de la cámara";
    }

    if (shouldLogout) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setTimeout(() => {
        if (window.location.pathname !== "/authentication/sign-in") {
          window.location.href = "/authentication/sign-in";
        }
      }, 2000);
    }

    return {
      success: false,
      error: errorMessage,
      data: {
        ip: ip || "No especificada",
        version: "Error",
        uptime: "Error",
        bootTime: "Error",
        cpuUtilization: "Error",
        memoryUsage: "Error",
        memoryAvailable: "Error",
        cacheSize: "Error",
        cameraRunTime: "Error",
        lastCalibrationTime: "Error",
        uploads: [],
        error: errorMessage,
      },
      message: errorMessage,
      timestamp: new Date().toISOString(),
      httpStatus: httpStatus,
      canRetry: true,
    };
  }
};

// ========== SERVICIO PARA OBTENER IMAGEN DE LA CÁMARA (VERSIÓN CORREGIDA) ==========
export const getCameraPictureService = async (ip) => {
  try {
    const authInfo = getAuthInfo();

    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!ip) {
      throw new Error("La IP de la cámara es requerida");
    }

    // CONFIGURACIÓN CRÍTICA: responseType: 'arraybuffer' para recibir datos binarios
    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "image/jpeg, application/json, */*",
        Authorization: `Bearer ${authInfo.token}`,
      },
      responseType: "arraybuffer", // ¡ESTO ES CLAVE! Para recibir datos binarios
      timeout: 30000,
    };

    const response = await axiosInstance.post(
      "/api/automatization/camera/picture-camera",
      { ip },
      config
    );

    const { data, status, headers } = response;

    let imageBase64 = null;
    let imageData = null;

    // CASO 1: Los datos son un ArrayBuffer (respuesta binaria directa)
    if (data instanceof ArrayBuffer) {
      const bytes = new Uint8Array(data);

      if (isValidImage(bytes)) {
        const base64Data = convertBinaryToBase64(bytes);
        const mimeType = detectImageMimeType(bytes);
        imageBase64 = `data:${mimeType};base64,${base64Data}`;
      } else {
        console.warn("⚠️ Los datos no son una imagen válida");
        // Intentar decodificar como texto para ver si hay un mensaje de error
        try {
          const textData = new TextDecoder().decode(bytes);

          if (textData.includes("error") || textData.includes("msm")) {
            const parsed = JSON.parse(textData);
            return {
              success: false,
              error: parsed.msm || parsed.message || "Error del servidor",
              data: null,
              httpStatus: status,
            };
          }
        } catch (e) {
          // No es JSON, continuar
        }
      }
    }
    // CASO 2: Los datos son un Uint8Array
    else if (data instanceof Uint8Array) {
      if (isValidImage(data)) {
        const base64Data = convertBinaryToBase64(data);
        const mimeType = detectImageMimeType(data);
        imageBase64 = `data:${mimeType};base64,${base64Data}`;
      }
    }
    // CASO 3: Los datos son un string (posiblemente base64 o binario)
    else if (typeof data === "string") {
      // Verificar si ya es una URL de datos base64
      if (data.startsWith("data:image")) {
        imageBase64 = data;
      }
      // Verificar si es base64 puro
      else if (/^[A-Za-z0-9+/=]+$/.test(data.substring(0, 100))) {
        imageBase64 = `data:image/jpeg;base64,${data}`;
      }
      // Es un string binario
      else {
        const bytes = new Uint8Array(data.length);
        for (let i = 0; i < data.length; i++) {
          bytes[i] = data.charCodeAt(i) & 0xff;
        }

        if (isValidImage(bytes)) {
          const base64Data = convertBinaryToBase64(bytes);
          const mimeType = detectImageMimeType(bytes);
          imageBase64 = `data:${mimeType};base64,${base64Data}`;
        } else {
          // Intentar parsear como JSON
          try {
            const parsed = JSON.parse(data);

            if (parsed.msm || parsed.message) {
              return {
                success: false,
                error: parsed.msm || parsed.message || "Error del servidor",
                data: null,
                httpStatus: status,
              };
            }
          } catch (e) {
            // No es JSON
          }
        }
      }
    }
    // CASO 4: Los datos son un objeto (respuesta JSON con datos anidados)
    else if (data && typeof data === "object") {
      // Buscar recursivamente los datos de la imagen
      const findImageData = (obj, depth = 0) => {
        if (depth > 5) return null;
        if (!obj || typeof obj !== "object") return null;

        // Si encontramos un array o string que parece datos de imagen
        for (const key of Object.keys(obj)) {
          const value = obj[key];

          // Array de bytes
          if (Array.isArray(value) && value.length > 1000) {
            return value;
          }
          // String largo que podría ser base64 o binario
          if (typeof value === "string" && value.length > 1000) {
            return value;
          }
          // Uint8Array
          if (value instanceof Uint8Array && value.length > 1000) {
            return value;
          }
          // ArrayBuffer
          if (value instanceof ArrayBuffer && value.byteLength > 1000) {
            return value;
          }
          // Buscar recursivamente
          if (typeof value === "object" && value !== null) {
            const found = findImageData(value, depth + 1);
            if (found) return found;
          }
        }
        return null;
      };

      imageData = findImageData(data);

      if (imageData) {
        let bytes = null;

        if (Array.isArray(imageData)) {
          bytes = new Uint8Array(imageData);
        } else if (imageData instanceof Uint8Array) {
          bytes = imageData;
        } else if (imageData instanceof ArrayBuffer) {
          bytes = new Uint8Array(imageData);
        } else if (typeof imageData === "string") {
          bytes = new Uint8Array(imageData.length);
          for (let i = 0; i < imageData.length; i++) {
            bytes[i] = imageData.charCodeAt(i) & 0xff;
          }
        }

        if (bytes && isValidImage(bytes)) {
          const base64Data = convertBinaryToBase64(bytes);
          const mimeType = detectImageMimeType(bytes);
          imageBase64 = `data:${mimeType};base64,${base64Data}`;
        }
      }

      // Si no encontramos imagen, verificar si hay mensaje de error
      if (!imageBase64 && (data.msm || data.message)) {
        return {
          success: false,
          error: data.msm || data.message || "Error del servidor",
          data: null,
          httpStatus: status,
        };
      }
    }

    if (imageBase64) {
      return {
        success: true,
        data: {
          ip: ip,
          imageBase64: imageBase64,
          imageUrl: imageBase64,
          timestamp: new Date().toISOString(),
        },
        message: "Imagen obtenida exitosamente",
        httpStatus: status,
      };
    }

    console.warn("⚠️ No se pudo obtener la imagen de la respuesta");
    return {
      success: false,
      error: "No se pudo obtener la imagen de la cámara",
      data: null,
      message: "Error al procesar la imagen",
      httpStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en getCameraPictureService:", error);

    let errorMessage = "Error al obtener la imagen de la cámara";
    let shouldLogout = false;
    let httpStatus = null;

    if (error.response) {
      httpStatus = error.response.status;

      // Intentar decodificar la respuesta si es arraybuffer
      if (error.response.data instanceof ArrayBuffer) {
        try {
          const textData = new TextDecoder().decode(error.response.data);
          const parsed = JSON.parse(textData);
          if (parsed.msm || parsed.message) {
            errorMessage = parsed.msm || parsed.message;
          }
        } catch (e) {
          errorMessage = `Error ${httpStatus}: ${error.response.statusText}`;
        }
      } else if (error.response.data?.msm) {
        errorMessage = error.response.data.msm;
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response.data === "string") {
        errorMessage = error.response.data;
      } else {
        errorMessage = `Error ${httpStatus}: ${error.response.statusText}`;
      }

      if (httpStatus === 401) {
        errorMessage = "No autorizado. Su sesión ha expirado.";
        shouldLogout = true;
      } else if (httpStatus === 404) {
        errorMessage = "No se encontró la cámara con la IP proporcionada.";
      } else if (httpStatus === 500) {
        errorMessage = "Error interno del servidor al obtener la imagen.";
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor. Verifique su conexión.";
      httpStatus = 0;
    } else {
      errorMessage = error.message || "Error desconocido";
    }

    if (shouldLogout) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setTimeout(() => {
        if (window.location.pathname !== "/authentication/sign-in") {
          window.location.href = "/authentication/sign-in";
        }
      }, 2000);
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      message: errorMessage,
      timestamp: new Date().toISOString(),
      httpStatus: httpStatus,
      canRetry: true,
    };
  }
};

// ========== SERVICIO PARA OBTENER CÁMARAS POR NOMBRE DE COMERCIO ==========
export const getCamerasByCommerceNameService = async (commerceName, camerasList = null) => {
  try {
    if (camerasList && Array.isArray(camerasList)) {
      const commerce = camerasList.find((c) => c.name.toLowerCase() === commerceName.toLowerCase());
      if (commerce) {
        return {
          success: true,
          data: commerce.cameras,
          message: "Cámaras encontradas",
        };
      }
    }

    const camerasListResult = await getCamerasListService();
    if (camerasListResult.success && camerasListResult.data.length > 0) {
      const commerce = camerasListResult.data.find(
        (c) => c.name.toLowerCase() === commerceName.toLowerCase()
      );
      if (commerce) {
        return {
          success: true,
          data: commerce.cameras,
          message: "Cámaras encontradas",
        };
      }
    }

    return {
      success: false,
      error: `No se encontró el comercio: ${commerceName}`,
      data: [],
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Error al obtener las cámaras del comercio",
      data: [],
    };
  }
};

// ========== SERVICIO PARA OBTENER INFORMACIÓN DE UNA CÁMARA POR SU IP ==========
export const getCameraInfoByIpService = async (ip, camerasList = null) => {
  try {
    if (camerasList && Array.isArray(camerasList)) {
      for (const comercio of camerasList) {
        const camera = comercio.cameras.find((c) => c.ip === ip);
        if (camera) {
          return {
            success: true,
            data: {
              ...camera,
              commerceName: comercio.name,
            },
            message: "Cámara encontrada",
          };
        }
      }
    }

    const camerasListResult = await getCamerasListService();
    if (camerasListResult.success && camerasListResult.data.length > 0) {
      for (const comercio of camerasListResult.data) {
        const camera = comercio.cameras.find((c) => c.ip === ip);
        if (camera) {
          return {
            success: true,
            data: {
              ...camera,
              commerceName: comercio.name,
            },
            message: "Cámara encontrada",
          };
        }
      }
    }

    return {
      success: false,
      error: `No se encontró ninguna cámara con la IP: ${ip}`,
      data: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Error al buscar la cámara por IP",
      data: null,
    };
  }
};

// ========== SERVICIO PARA VERIFICAR SI UNA CÁMARA ESTÁ ACTIVA ==========
export const checkCameraStatus = async (ip) => {
  try {
    const result = await getCameraStatusService(ip);
    if (result.success) {
      const isActive = true;
      return {
        success: true,
        data: {
          ip: ip,
          isActive: isActive,
          uptime: result.data.uptime,
          cpuUtilization: result.data.cpuUtilization,
          bootTime: result.data.bootTime,
        },
        message: isActive ? "Cámara activa" : "Cámara no responde",
      };
    }
    return {
      success: false,
      error: result.error,
      data: {
        ip: ip,
        isActive: null,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Error al verificar el estado de la cámara",
      data: {
        ip: ip,
        isActive: null,
      },
    };
  }
};

// ========== FUNCIÓN AUXILIAR PARA LIMPIAR URL DE IMAGEN ==========
export const revokeImageUrl = (imageUrl) => {
  if (imageUrl && imageUrl.startsWith("blob:")) {
    URL.revokeObjectURL(imageUrl);
  }
};

// ========== FUNCIÓN AUXILIAR PARA PREVISUALIZAR IMAGEN EN CONSOLA ==========
export const testImageConversion = async (binaryData) => {
  const base64 = convertBinaryToBase64(binaryData);
  if (base64) {
    const mimeType = detectImageMimeType(
      binaryData instanceof Uint8Array ? binaryData : new Uint8Array(binaryData)
    );
    const dataUrl = `data:${mimeType};base64,${base64}`;
    return dataUrl;
  }

  return null;
};

export default {
  getCamerasList: getCamerasListService,
  getCameraStatus: getCameraStatusService,
  getCameraPicture: getCameraPictureService,
  getCamerasByCommerceName: getCamerasByCommerceNameService,
  getCameraInfoByIp: getCameraInfoByIpService,
  checkCameraStatus: checkCameraStatus,
  revokeImageUrl: revokeImageUrl,
  testImageConversion: testImageConversion,
};
